/**
 * @module AnalysisArtefactsService
 * @purpose API client functions for the two analysis artefact endpoints.
 * @depends apiClient (services/api.ts), AnalysisArtefact types (types/analysis-artefacts.ts)
 * @dependedOnBy hooks/use-analysis-artefacts.ts
 *
 * @rules
 * - Performance: repeated-key query params via URLSearchParams.append — NOT csv-join
 * - State: stateless service functions; state lives in hooks
 * - Errors: ApiError propagates from apiClient — no try-catch swallowing
 */

import { apiClient } from './api'
import type {
  AnalysisArtefactPage,
  AnalysisFacets,
  AnalysisFilters,
} from '../types/analysis-artefacts'

/**
 * Parameters for the artefact listing endpoint.
 *
 * @behaviour cursor absent or null means first page; limit defaults to 200 on the server.
 * @errors n/a
 */
export interface ListArtefactsParams {
  cursor?: string | null
  limit?: number
  search?: string
  filters?: AnalysisFilters
}

/**
 * Parameters for the facet aggregation endpoint.
 *
 * @behaviour Filters mirror the listing endpoint so facet counts stay coherent.
 * @errors n/a
 */
export interface GetFacetsParams {
  search?: string
  filters?: AnalysisFilters
}

/**
 * GET /api/cases/:caseId/artefacts — cursor-paginated artefact listing.
 *
 * @behaviour Returns the first page when cursor is absent; nextCursor is null on the last page.
 * @errors Propagates ApiError from apiClient (401, 403, 404, 500).
 */
export async function listArtefacts(
  caseId: string,
  params: ListArtefactsParams = {},
): Promise<AnalysisArtefactPage> {
  const qs = buildArtefactQueryString(params)
  return apiClient.get<AnalysisArtefactPage>(`/cases/${caseId}/artefacts${qs}`)
}

/**
 * GET /api/cases/:caseId/artefacts/facets — per-category filter facet counts.
 *
 * @behaviour ipRanges is null in the response when no netstat rows exist in the QC-gated set.
 * @errors Propagates ApiError from apiClient (401, 403, 404, 500).
 */
export async function getFacets(
  caseId: string,
  params: GetFacetsParams = {},
): Promise<AnalysisFacets> {
  const qs = buildArtefactQueryString(params)
  return apiClient.get<AnalysisFacets>(`/cases/${caseId}/artefacts/facets${qs}`)
}

interface ArtefactQueryParams {
  cursor?: string | null
  limit?: number
  search?: string
  filters?: AnalysisFilters
}

/**
 * Serialise artefact query params to a query string.
 * Multi-value filter fields use repeated keys (sp.append per element)
 * because serde_html_form on the backend expects ?artefactType=mft&artefactType=pslist,
 * NOT the comma-joined form ?artefactType=mft,pslist.
 *
 * @behaviour Returns empty string when no params produce output.
 * @errors n/a
 */
function buildArtefactQueryString(params: ArtefactQueryParams): string {
  const search = new URLSearchParams()
  appendScalarParams(search, params)
  appendFilterParams(search, params.filters ?? {})
  const qs = search.toString()
  return qs ? `?${qs}` : ''
}

function appendScalarParams(
  search: URLSearchParams,
  params: Pick<ArtefactQueryParams, 'cursor' | 'limit' | 'search'>,
): void {
  if (params.cursor) search.append('cursor', params.cursor)
  if (params.limit !== undefined) search.append('limit', String(params.limit))
  if (params.search) search.append('search', params.search)
}

type FilterKey = keyof AnalysisFilters

const FILTER_PARAM_NAMES: Record<FilterKey, string> = {
  artefactType: 'artefactType',
  endpointId: 'endpointId',
  username: 'username',
  timeBucket: 'timeBucket',
  ipRange: 'ipRange',
}

function appendFilterParams(
  search: URLSearchParams,
  filters: AnalysisFilters,
): void {
  const keys = Object.keys(FILTER_PARAM_NAMES) as FilterKey[]
  for (const key of keys) {
    const values = filters[key]
    if (!values) continue
    for (const value of values) search.append(FILTER_PARAM_NAMES[key], value)
  }
}
